import type { ReactNode } from 'react';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon/Icon';
import { OpenWord } from '../OpenWord/OpenWord';

/**
 * Empty is a state, not an accident: it says what is missing and offers the one
 * action that fixes it.
 */
export interface EmptyStateProps {
  readonly icon?: IconName | undefined;
  readonly title: string;
  readonly description?: string | undefined;
  readonly action?: ReactNode | undefined;
  readonly className?: string | undefined;
  /**
   * The OPEN id of words nobody has decided yet (DESIGN.md D4, Part 3 item 6): the title and the
   * description are drawn dotted under it (`OpenWord`).
   */
  readonly openWord?: string | undefined;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  openWord,
}: EmptyStateProps) {
  const words = (text: string) =>
    openWord === undefined ? text : <OpenWord id={openWord}>{text}</OpenWord>;
  return (
    <div
      className={cx(
        'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        className,
      )}
    >
      {icon !== undefined && <Icon name={icon} size="1.75rem" className="text-ink-subtle" />}
      <div className="space-y-1">
        <p className="text-sm font-medium text-ink">{words(title)}</p>
        {description !== undefined && (
          <p className="mx-auto max-w-prose text-sm text-ink-muted">{words(description)}</p>
        )}
      </div>
      {action}
    </div>
  );
}
